import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Callout,
  EmptyState,
  Spinner,
  Table,
  Tag,
  TextField,
  type TableColumn,
} from '@humanforce/mango-ui-react';
import type {
  PayRunPopulationCheckResult,
  PayRunPopulationPage,
  PayRunPopulationRefreshAccepted,
  PopulationLoadStatus,
} from './payRunPopulationApiTypes';
import type { PayRunCalculationStatus } from './payRunsApiTypes';
import {
  checkPopulationChanges,
  getPopulation,
  refreshPopulation,
} from './payRunPopulationHttpClient';

interface PayRunEmployeesTabProps {
  payId: string;
  workflowStatus: string;
  calculationStatus?: PayRunCalculationStatus;
  populationStatus: PopulationLoadStatus | null | undefined;
  canRefreshPopulation: boolean;
  onRefreshAccepted?: (accepted: PayRunPopulationRefreshAccepted) => void;
}

const pageSize = 20;
const unlockedStatuses = new Set(['open', 'payroll_active']);
const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const number = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 2 });

const words = (value: string): string => value
  .split('_')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const lockedStatusLabel = (value: string): string =>
  value === 'ready_to_pay' ? 'Ready to Pay' : words(value);

export const PayRunEmployeesTab = ({
  payId,
  workflowStatus,
  calculationStatus = 'not_ready',
  populationStatus,
  canRefreshPopulation,
  onRefreshAccepted,
}: PayRunEmployeesTabProps) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [result, setResult] = useState<PayRunPopulationPage | null>(null);
  const [change, setChange] = useState<PayRunPopulationCheckResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      setResult(await getPopulation(payId, { page, pageSize, search }, signal));
    } catch (loadError) {
      if (!signal?.aborted) {
        setError(loadError instanceof Error ? loadError.message : 'Employees could not be loaded.');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page, payId, search]);

  const check = useCallback(async (signal?: AbortSignal) => {
    setChecking(true);
    try {
      setChange(await checkPopulationChanges(payId, signal));
    } catch {
      if (!signal?.aborted) setChange(null);
    } finally {
      if (!signal?.aborted) setChecking(false);
    }
  }, [payId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, populationStatus]);

  useEffect(() => {
    const controller = new AbortController();
    void check(controller.signal);
    return () => controller.abort();
  }, [check, populationStatus]);

  const columns = useMemo<TableColumn[]>(() => {
    const calculated = calculationStatus === 'complete';
    const calculatedMoney = (value: number) => calculated ? currency.format(value) : '—';
    return [
    {
      field: 'displayName',
      header: 'Employee',
      body: (row) => (
        <span className="mgo-stack mgo-gap-2xs">
          <strong>{row.displayName}</strong>
          <span className="mgo-text-xs mgo-color-muted">{row.employeeNumber}</span>
        </span>
      ),
    },
    { field: 'roleTitle', header: 'Role', body: (row) => row.roleTitle || '—' },
    { field: 'units', header: 'Units', body: (row) => number.format(row.units) },
    { field: 'hourlyUnits', header: 'Hrly units', body: (row) => number.format(row.hourlyUnits) },
    { field: 'gross', header: 'Gross', body: (row) => calculatedMoney(row.gross) },
    { field: 'taxable', header: 'Taxable', body: (row) => calculatedMoney(row.taxable) },
    { field: 'tax', header: 'Tax', body: (row) => calculatedMoney(row.tax) },
    { field: 'stsl', header: 'STSL', body: () => '—' },
    { field: 'btd', header: 'BTD', body: () => '—' },
    { field: 'super', header: 'Super', body: () => '—' },
    { field: 'atd', header: 'ATD', body: () => '—' },
    { field: 'netPay', header: 'Net pay', body: (row) => calculatedMoney(row.netPay) },
    {
      field: 'exceptionSeverity',
      header: 'Excn',
      body: (row) => row.exceptionSeverity
        ? <Tag label={words(row.exceptionSeverity)} tone={row.exceptionSeverity === 'blocking' ? 'critical' : 'caution'} />
        : <Tag label="Clear" tone="positive" />,
    },
    ];
  }, [calculationStatus]);

  const apply = async () => {
    setApplying(true);
    setActionError(null);
    try {
      const accepted = await refreshPopulation(payId);
      onRefreshAccepted?.(accepted);
    } catch (refreshError) {
      setActionError(refreshError instanceof Error ? refreshError.message : 'Employee changes could not be applied.');
    } finally {
      setApplying(false);
    }
  };

  const unlocked = unlockedStatuses.has(workflowStatus);
  const hasActivePopulation = result?.revisionNumber != null;
  const showAction = canRefreshPopulation && unlocked && !checking && !applying
    && populationStatus !== 'loading'
    && (!hasActivePopulation || change?.changeAvailable === true);

  return (
    <section aria-label="Employee population" className="mgo-stack mgo-gap-l" aria-busy={loading || applying}>
      <div className="mgo-split mgo-align-end mgo-gap-m">
        <TextField
          label="Search employees"
          placeholder="Search employees, role or employee number"
          leadingIcon="search"
          clearable
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(1); }}
          onClear={() => { setSearch(''); setPage(1); }}
        />
        {result && <span className="mgo-text-s mgo-color-muted">{result.employeeCount} employees</span>}
      </div>

      {populationStatus === 'loading' && (
        <Callout tone="info" title="Applying employee changes">
          <span className="mgo-cluster mgo-gap-s"><Spinner /> A refreshed employee snapshot is being prepared.</span>
        </Callout>
      )}

      {change?.changeAvailable && (
        <Callout
          tone="caution"
          title="Employee changes available"
          action={showAction
            ? <Button label="Apply employee changes" icon="refresh" loading={applying} onClick={() => void apply()} />
            : undefined}
        >
          {unlocked
            ? canRefreshPopulation
              ? `${change.uniquePersonCount} employees are available in the latest HR snapshot.`
              : `${change.uniquePersonCount} employees are available; a Payroll Administrator can apply them.`
            : `Changes cannot be applied because this Pay Run is locked at ${lockedStatusLabel(workflowStatus)}.`}
        </Callout>
      )}

      {actionError && <Callout tone="critical" title="Employee changes not applied">{actionError}</Callout>}
      {error && (
        <Callout
          tone="critical"
          title="Employees unavailable"
          action={<Button label="Retry" variant="secondary" size="small" onClick={() => void load()} />}
        >
          {error}
        </Callout>
      )}

      {loading && !error ? (
        <div className="mgo-cluster mgo-justify-center mgo-gap-s" style={{ minHeight: '12rem' }}>
          <Spinner /> <span>Loading employees…</span>
        </div>
      ) : !error && !hasActivePopulation ? (
        <EmptyState
          variant="no-data"
          title="No employee population loaded"
          description={canRefreshPopulation
            ? 'Choose the employee and Timesheet sources for this Pay Run.'
            : 'A Payroll Administrator must load the employee population for this Pay Run.'}
        />
      ) : !error && result && result.items.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No employees match your search"
          description="Change or clear the current search."
          actions={[<Button key="clear" label="Clear search" variant="secondary" onClick={() => { setSearch(''); setPage(1); }} />]}
        />
      ) : !error && result ? (
        <div style={{ overflowX: 'auto' }}>
          <Table value={result.items} columns={columns} scrollable emptyMessage="No employees." />
        </div>
      ) : null}

      {!loading && !error && result && result.pagination.totalPages > 1 && (
        <nav aria-label="Employee pages" className="mgo-cluster mgo-justify-end mgo-gap-s">
          <span className="mgo-text-xs mgo-color-muted">
            Page {result.pagination.page} of {result.pagination.totalPages}
          </span>
          <Button label="Previous" variant="secondary" size="small" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} />
          <Button label="Next" variant="secondary" size="small" disabled={page >= result.pagination.totalPages} onClick={() => setPage((current) => current + 1)} />
        </nav>
      )}
    </section>
  );
};
